'use strict';

import getVideoId from 'get-video-id';
import Player from '@vimeo/player';

import parseBoolean from '../utils/parse-boolean';

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

let youtubeApiPromise;

function loadYouTubeAPI() {
    if (youtubeApiPromise) {
        return youtubeApiPromise;
    }

    youtubeApiPromise = new Promise(resolve => { // eslint-disable-line compat/compat
        if (window.YT?.Player) {
            resolve();
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';

        const previous = window.onYouTubeIframeAPIReady;

        window.onYouTubeIframeAPIReady = () => {
            previous?.();
            resolve();
        }

        document.head.appendChild(script);
    });

    return youtubeApiPromise;
}

function createYouTubePlayer(url, id) {
    let player;
    let events = {}
    let rafId = null;
    let duration = null;
    let realDuration = null;

    const _emit = (event, data) => {
        events[event]?.forEach(cb => cb(data));
    }

    const _startUpdate = () => {
        const tick = () => {
            if (!player) {
                rafId = null;
                return;
            }

            let currentTime = player.getCurrentTime();

            if (!realDuration) {
                currentTime *= 1.075;
            }

            const effectiveDuration = realDuration || duration;
            const percent = Math.min(currentTime / effectiveDuration * 100, 100);

            _emit('progress', percent);

            rafId = requestAnimationFrame(tick); // eslint-disable-line compat/compat
        }

        if (!rafId) {
            rafId = requestAnimationFrame(tick); // eslint-disable-line compat/compat
        }
    }

    const _stopUpdate = () => {
        if (rafId) {
            cancelAnimationFrame(rafId);
            rafId = null;
        }
    }

    return {
        async mount(container) {
            await loadYouTubeAPI();

            const dummy = document.createElement('div');
            dummy.id = `${container.id}-player`;
            container.appendChild(dummy);

            return new Promise(resolve => { // eslint-disable-line
                player = new YT.Player(dummy.id, { // eslint-disable-line no-undef
                    videoId: id,
                    playerVars: {
                        controls: 0,
                        autoplay: 0,
                        mute: 1,
                        disablekb: 1,
                        enablejsapi: 1,
                        iv_load_policy: 3,
                        modestbranding: 1,
                        playsinline: 1,
                        showinfo: 0,
                        rel: 0,
                        fs: 0,
                        cc_load_policy: 0,
                        height: 1200,
                        width: 675,
                        autohide: 1,
                        loop: parseBoolean(container.dataset.loop),
                        playlist: id,
                    },
                    events: {
                        onReady: () => {
                            duration = player.getDuration();
                            _emit('loaded');
                            resolve();
                        },
                        onStateChange: event => {
                            if (event.data === YT.PlayerState.ENDED) { // eslint-disable-line no-undef
                                if (!realDuration) {
                                    realDuration = player.getCurrentTime();
                                }

                                _emit('progress', 100);
                                _emit('ended');
                                _stopUpdate();
                            } else if (event.data === YT.PlayerState.PLAYING) { // eslint-disable-line no-undef
                                _emit('play');
                                _startUpdate();
                            } else if (event.data === YT.PlayerState.PAUSED) { // eslint-disable-line no-undef
                                _emit('pause');
                                _stopUpdate();
                            }
                        },
                    },
                });
            });
        },

        async play() {
            return player.playVideo();
        },

        async pause() {
            return player.pauseVideo();
        },

        async stop() {
            player.pauseVideo();
            player.seekTo(0);
            _emit('progress', 0);
        },

        async reset() {
            player.seekTo(0);
            _emit('progress', 0);
        },

        async isPlaying() {
            return player.getPlayerState() === YT.PlayerState.PLAYING; // eslint-disable-line no-undef
        },

        on(event, callback) {
            (events[event] ||= []).push(callback);
        },

        off(event, callback) {
            if (!events[event]) {
                return;
            }

            events[event] = events[event].filter(cb => cb !== callback);
        },
    }
}

function createVimeoPlayer(url, id) {
    let player;
    let events = {};

    let rafId = null;
    let duration = null;
    let realDuration = null;

    const _emit = (event, data) => {
        events[event]?.forEach(cb => cb(data));
    }

    const _startUpdate = () => {
        const tick = async () => {
            if (!player) {
                rafId = null;
                return;
            }

            let currentTime = await player.getCurrentTime();

            if (!realDuration) {
                currentTime *= 1.075;
            }

            const effectiveDuration = realDuration || duration;
            const percent = Math.min(currentTime / effectiveDuration * 100, 100);

            _emit('progress', percent);

            rafId = requestAnimationFrame(tick); // eslint-disable-line compat/compat
        }

        if (!rafId) {
            rafId = requestAnimationFrame(tick); // eslint-disable-line compat/compat
        }
    }

    const _stopUpdate = () => {
        if (rafId) {
            cancelAnimationFrame(rafId);
            rafId = null;
        }
    }

    return {
        async mount(container) {
            return new Promise(resolve => { // eslint-disable-line compat/compat
                player = new Player(container, {
                    url,
                    background: false,
                    controls: false,
                    muted: true,
                    autoplay: false,
                    preload: 'auto',
                    rel: 0,
                    loop: parseBoolean(container.dataset.loop),
                });

                player.ready().then(async () => {
                    // Vimeo loop fix
                    player.setLoop(parseBoolean(container.dataset.loop));
                });

                player.getDuration().then(value => {
                    duration = value;
                });

                player.on('loaded', () => {
                    _emit('loaded');
                    resolve();
                });

                player.on('play', () => {
                    _emit('play');
                    _startUpdate();
                });

                player.on('pause', () => {
                    _emit('pause');
                    _stopUpdate();
                });

                player.on('ended', data => {
                    if (!realDuration) {
                        realDuration = data.duration;
                    }

                    _emit('progress', 100);
                    _emit('ended');
                    _stopUpdate();
                });
            });
        },

        async play() {
            await player.play();
        },

        async pause() {
            await player.pause();
        },

        async stop() {
            await player.pause();
            await player.setCurrentTime();
            _emit('progress', 0);
        },

        async reset() {
            await player.setCurrentTime(0);
            _emit('progress', 0);
        },

        async isPlaying() {
            return player.getPaused().then(isPaused => !isPaused);
        },

        on(event, callback) {
            (events[event] ||= []).push(callback);
        },

        off(event, callback) {
            if (!events[event]) {
                return;
            }

            events[event] = events[event].filter(cb => cb !== callback);
        },
    }
}

function createHtml5Player(url) {
    let video;
    let events = {};
    let rafId = null;

    const _emit = (event, data) => {
        events[event]?.forEach(cb => cb(data));
    }

    const _startUpdate = () => {
        const tick = () => {
            if (!video) {
                rafId = null;
                return;
            }

            const percent = video.duration ? Math.min(video.currentTime / video.duration * 100, 100) : 0;
            _emit('progress', percent);
            rafId = requestAnimationFrame(tick); // eslint-disable-line compat/compat
        }

        if (!rafId) {
            rafId = requestAnimationFrame(tick); // eslint-disable-line compat/compat
        }
    }

    const _stopUpdate = () => {
        if (rafId) {
            cancelAnimationFrame(rafId);
            rafId = null;
        }
    }

    return {
        async mount(container) {
            if (video) return;

            return new Promise(resolve => { // eslint-disable-line compat/compat
                video = document.createElement('video');
                video.src = url;
                video.load();
                video.muted = true;
                video.playsInline = true;
                video.preload = 'auto';
                video.loop = parseBoolean(container.dataset.loop);
                video.setAttribute('playsinline', '');
                video.setAttribute('muted', '');
                video.setAttribute('autoplay', '');

                video.addEventListener('loadedmetadata', () => {
                    _emit('loaded');
                    resolve();
                }, { once: true });

                video.addEventListener('play', () => {
                    _emit('play');
                    _startUpdate();
                });

                video.addEventListener('pause', () => {
                    _emit('pause');
                    _stopUpdate();
                });

                video.addEventListener('ended', () => {
                    _emit('progress', 100);
                    _emit('ended');
                    _stopUpdate();
                });

                container.appendChild(video);
            });
        },

        async play() {
            return video?.play();
        },

        async pause() {
            return video?.pause();
        },

        async stop() {
            video?.pause();
            if (video) video.currentTime = 0;
            _emit('progress', 0);
        },

        async reset() {
            if (video) video.currentTime = 0;
            _emit('progress', 0);
        },

        async isPlaying() {
            return video ? !video.paused : false;
        },

        on(event, callback) {
            (events[event] ||= []).push(callback);
        },

        off(event, callback) {
            if (!events[event]) return;
            events[event] = events[event].filter(cb => cb !== callback);
        },
    }
}

const PROVIDERS = {
    youtube: {
        createPlayer: createYouTubePlayer,
    },

    vimeo: {
        createPlayer: createVimeoPlayer,
    },

    html5: {
        createPlayer: createHtml5Player,
    },
}

const directVideoExt = /\.(mp4|webm|ogg)(\?.*)?$/i;

function determineService(url) {
    let { id, service } = getVideoId(url);

    if (!service && directVideoExt.test(url)) {
        service = 'html5';
    }

    let provider = PROVIDERS[service];

    if (!provider) {
        return null;
    }

    return {
        id,
        service,
        player() {
            return provider.createPlayer(url, id, this.oembed);
        },
    }
}

export async function initialiseVideo(element) {
    const videoContainer = element.querySelector('.video-container');

    if (!videoContainer) {
        return;
    }

    const provider = determineService(videoContainer.dataset.videoUrl);

    if (!provider) {
        return;
    }

    // Cache frequently used booleans
    const loop = parseBoolean(videoContainer.dataset.loop);
    const autoplay = parseBoolean(videoContainer.dataset.autoplay);

    // Cache play buttons and last play state
    const playButtons = [...element.querySelectorAll('.page-header__play-pause')];

    let playState = null;

    const toggleButtonState = isPlaying => {
        if (playState === isPlaying) {
            return; // avoid unnecessary DOM updates
        }

        playState = isPlaying;

        for (const btn of playButtons) {
            const icon = btn.querySelector('.icon');
            const label = btn.querySelector('.label');

            if (isPlaying) {
                icon.innerHTML = '<svg class="o-icon o-icon--pause"><use xlink:href="#pause"></use></svg>';
                label.textContent = 'Pause';
            } else {
                icon.innerHTML = '<svg class="o-icon o-icon--play"><use xlink:href="#play"></use></svg>';
                label.textContent = 'Play';
            }
        }
    }

    const player = provider.player();

    // Mount player asynchronously
    return player.mount(videoContainer).then(() => {
        // videoContainer is aria-hidden (decorative background video); keep its
        // injected embed out of the tab order too
        videoContainer.querySelectorAll('iframe, video').forEach(el => el.setAttribute('tabindex', '-1'));

        // Attach click listeners once, minimal DOM work
        for (const btn of playButtons) {
            btn.addEventListener('click', async () => {
                (await player.isPlaying()) ? player.pause() : player.play();
            });
        }

        let initialPlay = true;

        // Player events
        player.on('loaded', () => {
            videoContainer.classList.add('is-loaded');
            element.classList.add('is-loaded');
            initialPlay = false;
        });

        player.on('play', () => {
            if (initialPlay) {
                videoContainer.classList.add('is-loaded');
                element.classList.add('is-loaded');
                initialPlay = false;
            }

            toggleButtonState(true);
        });

        player.on('pause', () => {
            toggleButtonState(false);
        });

        player.on('ended', () => {
            if (loop) {
                player.play(); // automatically loop
            }
        });

        // Autoplay if allowed
        if (!prefersReducedMotion && autoplay) {
            player.play();
        }

        return player;
    });
}

export default async function () {
    const headerElements = document.querySelectorAll('.js-header-video');

    for (const headerElement of headerElements) {
        initialiseVideo(headerElement);
    }
}
